/**
 * The glyph for an investable, mapped from the name the API stores.
 *
 * Named imports rather than a computed lookup off the package namespace: a
 * computed lookup defeats tree-shaking and drags all 2,659 IconPark icons into
 * the bundle. `pound` is not IconPark's — its own `pound` is the # key, not
 * sterling — so sterling is drawn from the design system's own £ below.
 *
 * The catalogue is duplicated from @ifgengineering/api-contracts, which the API
 * validates writes against and Nucleus builds its picker from. This app does
 * not depend on that package, so `investable-icons.test.ts` reads the
 * contract's source and fails if the two ever drift apart — the same guard
 * cur8-mob uses for its own copy.
 */
import {
  AirplaneWindow,
  BalanceTwo,
  BankCard,
  BatteryFull,
  Bitcoin,
  Blockchain,
  Brain,
  BuildingOne,
  Car,
  ChartLine,
  ChartPie,
  Chip,
  City,
  Code,
  Data,
  DegreeHat,
  Diamond,
  Dollar,
  Exchange,
  FactoryBuilding,
  Funds,
  GreenNewEnergy,
  Handbag,
  Health,
  Helmet,
  Home,
  Homestay,
  MedicalBox,
  OilIndustry,
  PaperMoneyTwo,
  PeopleSafe,
  Planet,
  Robot,
  Rocket,
  Shop,
  SolarEnergy,
  StockMarket,
  Tree,
  TrendTwo,
  Truck,
  Wallet,
} from "@icon-park/react";
import type { Icon } from "@icon-park/react/es/runtime";

export const INVESTABLE_ICONS = [
  "pound",
  "dollar",
  "paper-money-two",
  "funds",
  "wallet",
  "bitcoin",
  "chart-line",
  "trend-two",
  "chart-pie",
  "stock-market",
  "bank-card",
  "exchange",
  "balance-two",
  "handbag",
  "building-one",
  "city",
  "home",
  "homestay",
  "factory-building",
  "helmet",
  "green-new-energy",
  "solar-energy",
  "battery-full",
  "oil-industry",
  "tree",
  "brain",
  "chip",
  "robot",
  "code",
  "data",
  "blockchain",
  "rocket",
  "medical-box",
  "health",
  "degree-hat",
  "people-safe",
  "car",
  "truck",
  "airplane-window",
  "planet",
  "shop",
  "diamond",
] as const;

export type InvestableIcon = (typeof INVESTABLE_ICONS)[number];

export const INVESTABLE_GLYPHS: Record<Exclude<InvestableIcon, "pound">, Icon> =
  {
    dollar: Dollar,
    "paper-money-two": PaperMoneyTwo,
    funds: Funds,
    wallet: Wallet,
    bitcoin: Bitcoin,
    "chart-line": ChartLine,
    "trend-two": TrendTwo,
    "chart-pie": ChartPie,
    "stock-market": StockMarket,
    "bank-card": BankCard,
    exchange: Exchange,
    "balance-two": BalanceTwo,
    handbag: Handbag,
    "building-one": BuildingOne,
    city: City,
    home: Home,
    homestay: Homestay,
    "factory-building": FactoryBuilding,
    helmet: Helmet,
    "green-new-energy": GreenNewEnergy,
    "solar-energy": SolarEnergy,
    "battery-full": BatteryFull,
    "oil-industry": OilIndustry,
    tree: Tree,
    brain: Brain,
    chip: Chip,
    robot: Robot,
    code: Code,
    data: Data,
    blockchain: Blockchain,
    rocket: Rocket,
    "medical-box": MedicalBox,
    health: Health,
    "degree-hat": DegreeHat,
    "people-safe": PeopleSafe,
    car: Car,
    truck: Truck,
    "airplane-window": AirplaneWindow,
    planet: Planet,
    shop: Shop,
    diamond: Diamond,
  };

/** What an investable shows when it has no icon of its own. */
export const defaultInvestableIcon = (
  investableType?: string | null
): InvestableIcon =>
  investableType === "INCOME" ? "paper-money-two" : "chart-line";

const isKnown = (value: string): value is InvestableIcon =>
  (INVESTABLE_ICONS as readonly string[]).includes(value);

/**
 * The icon to draw. An unknown name — one added to the catalogue after this
 * build shipped — draws the default rather than an empty tile.
 */
export const resolveInvestableIcon = (
  icon?: string | null,
  investableType?: string | null
): InvestableIcon =>
  icon && isKnown(icon) ? icon : defaultInvestableIcon(investableType);
